import { enforce, omitWhen, test } from 'vest';

import {
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface DuveService {
  webhookSecret: string;
  checkInStatuses: string[];
  checkOutStatuses: string[];
  timezone: string;
  checkInTime: string;
  checkOutTime: string;
}

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
const WEBHOOK_SECRET = /^[A-Za-z0-9]{12}$/;

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    webhookSecret: '',
    checkInStatuses: [],
    checkOutStatuses: [],
    timezone: '',
    checkInTime: '12:00:00',
    checkOutTime: '10:00:00',
  },
  features: {},
  cron: [],
  webhooks: [{ name: 'standard', enabled: true }],
});

export const duve: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as DuveService;

    test(`${SERVICE}.webhookSecret`, t('validations.required'), () => {
      enforce(service.webhookSecret).isNotBlank();
    });

    omitWhen(!service.webhookSecret, () => {
      test(`${SERVICE}.webhookSecret`, t('validations.invalid'), () => {
        enforce(service.webhookSecret).matches(WEBHOOK_SECRET);
      });
    });

    for (const field of ['checkInStatuses', 'checkOutStatuses'] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        const statuses = Array.isArray(service[field]) ? service[field] : [];

        enforce(statuses).isNotEmpty();
        enforce(statuses.every((status) => Boolean(status?.trim()))).isTruthy();
      });
    }

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });

    for (const field of ['checkInTime', 'checkOutTime'] as const) {
      omitWhen(!service[field], () => {
        test(`${SERVICE}.${field}`, t('validations.invalid'), () => {
          enforce(service[field]).matches(TIME_OF_DAY);
        });
      });
    }
  },
};
